import type { AnchorHTMLAttributes, ReactNode } from 'react'

import { useRef } from 'react'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { gsap, registerGsap, wfEase, prefersReducedMotion } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface AnimatedLinkProps
    extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    children: ReactNode
    className?: string
    underlineClassName?: string
}

const isExternal = (href: string) => /^(https?:|mailto:|tel:|#)/.test(href)

export function AnimatedLink({
    href,
    children,
    className,
    underlineClassName,
    onMouseEnter,
    onMouseLeave,
    ...rest
}: AnimatedLinkProps) {
    const underlineRef = useRef<HTMLDivElement>(null)

    useIsomorphicLayoutEffect(() => {
        const el = underlineRef.current
        if (!el) return
        registerGsap()
        if (!prefersReducedMotion()) gsap.set(el, { xPercent: -102 })
    }, [])

    const handleEnter: typeof onMouseEnter = (event) => {
        onMouseEnter?.(event)
        const el = underlineRef.current
        if (!el || prefersReducedMotion()) return
        gsap.killTweensOf(el)
        gsap.to(el, { xPercent: 0, duration: 0.4, ease: wfEase('ease') })
    }

    const handleLeave: typeof onMouseLeave = (event) => {
        onMouseLeave?.(event)
        const el = underlineRef.current
        if (!el || prefersReducedMotion()) return
        gsap.killTweensOf(el)
        gsap.to(el, {
            xPercent: 100,
            duration: 0.4,
            ease: wfEase('ease'),
            onComplete: () => gsap.set(el, { xPercent: -102 })
        })
    }

    const inner = (
        <>
            {children}
            <div
                ref={underlineRef}
                className={cn('link-underline', underlineClassName)}
                style={{ transform: 'translateX(-102%)' }}
            />
        </>
    )

    if (isExternal(href)) {
        return (
            <a
                href={href}
                className={cn('w-inline-block', className)}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                {...rest}
            >
                {inner}
            </a>
        )
    }

    return (
        <Link
            href={href}
            className={cn('w-inline-block', className)}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            {...rest}
        >
            {inner}
        </Link>
    )
}
